import { Box, Tab, Tabs, styled } from '@mui/material'
import { ReactNode, SyntheticEvent, useState } from 'react'
import { a11yProps } from '../../app/utils'
import TabPanel from '../TabPanel/TabPanel'

export interface TabItem {
  label: string | ReactNode
  content: ReactNode
  disabled?: boolean
  hasError?: boolean
  withBorder?: boolean
}

export interface TabWrapperProps {
  tabs: TabItem[]
  ariaLabel?: string
  defaultTab?: number
  variant?: 'standard' | 'scrollable' | 'fullWidth'
  tabAlignment?: 'left' | 'center' | 'right'
  onChange?: (index: number) => void
}

const StyledTabs = styled(Tabs)<{ tabAlignment?: 'left' | 'center' | 'right' }>(({ theme, tabAlignment = 'left' }) => ({
  '& .MuiTabs-indicator': {
    backgroundColor: theme.palette.secondary.main,
  },
  '& .MuiTabs-flexContainer': {
    justifyContent: tabAlignment === 'center' ? 'center' : tabAlignment === 'right' ? 'flex-end' : 'flex-start',
  },
}))

const ErrorTab = styled(Tab)(({ theme }) => ({
  backgroundColor: 'rgb(211, 47, 47, 20%)',
  borderBottom: '4px solid rgb(211, 47, 47, 70%)',
  borderRadius: '4px 4px 0 0',
  color: theme.palette.error.main,
  '&.Mui-selected': {
    color: theme.palette.error.main,
  }
}))

const TabWrapper = ({
  tabs,
  ariaLabel = 'scrollable tabs',
  defaultTab = 0,
  variant = 'standard',
  tabAlignment = 'left',
  onChange,
}: TabWrapperProps) => {

  const [activeTab, setActiveTab] = useState(defaultTab)

  const handleChange = (_event: SyntheticEvent, tabId: number) => {
    setActiveTab(tabId)
    onChange?.(tabId)
  }

  return (
    <Box sx={{ width: '100%' }}>
      <StyledTabs
        value={activeTab}
        onChange={handleChange}
        variant={variant}
        scrollButtons={variant === 'scrollable'}
        textColor='secondary'
        aria-label={ariaLabel}
        tabAlignment={tabAlignment}
        sx={{
          pb: '16px',
        }}
      >
        {tabs.map((tab, index) =>
          tab.hasError ? (
            <ErrorTab key={`tab-${index}-${tab.label}`} label={tab.label} disabled={tab.disabled} {...a11yProps(index)} />
          ) : <Tab key={`tab-${index}-${tab.label}`} label={tab.label} disabled={tab.disabled} {...a11yProps(index)} />
        )}
      </StyledTabs>
      {tabs.map((tab, index) => (
        <TabPanel key={`panel-${index}-${tab.label}`} value={activeTab} index={index}>
          {tab.withBorder ? (
            <Box sx={{ border: '1px solid', marginTop: '-16px', borderColor: '#ccc', backgroundColor: '#ffffff', borderRadius: '0 0 8px 8px', width: '100%' }}>
              {tab.content}
            </Box>
          ) : (
            tab.content
          )}
        </TabPanel>
      ))}
    </Box>
  )
}

export default TabWrapper
